import React, { useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { I18N, I18NContext, I18NConsumer, GlobalContext, EBPagination, Loading, NotFound, EBModal } from 'commons';
import { Table } from '@eb/antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudDownloadAlt,
  faLink
} from '@fortawesome/free-solid-svg-icons';
import { Ajax, baseUrl, orgId, setPageTitle, formatLocalDate } from 'utils';
import './files-library.scss';

const FilesLibrary = ({ path, activeName }) => {
  const i18n = useContext(I18NContext);
  const { organization: { orgName }, onPaginationChange, pageNo: contextPageNo, pageSize: contextPageSize, navConfig: { showPublicFilesLibrary } } = useContext(GlobalContext);

  // Consolidated state for pagination
  const [pagination, setPagination] = useState({
    current: contextPageNo || 1,
    pageSize: contextPageSize || 10,
    total: 0
  });

  // Component state
  const [files, setFiles] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalContent, setErrorModalContent] = useState('');

  const getFileIcon = (_, documentType) =>
    documentType === 'Link' ? faLink : faCloudDownloadAlt;

  const toggleErrorModal = () => {
    setErrorModalOpen(!errorModalOpen);
  };

  // Fetch files data (memoized with useCallback)
  const fetchFiles = useCallback((page = pagination.current, size = pagination.pageSize) => {
    // Skip API call if feature flag is off
    if (!showPublicFilesLibrary) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setNotFound(false);

    Ajax.get("/rest/files/public-documents", {
      params: {
        organizationId: orgId,
        pageNumber: page,
        pageSize: size
      }
    })
      .then((response) => {
        let fileData = [];
        let totalCount = 0;

        // Check for empty or invalid response - might indicate feature is off
        if (!response || !response.data) {
          setNotFound(true);
          return;
        }

        if (Array.isArray(response.data)) {
          fileData = response.data;
          totalCount = fileData.length;
        } else if (response.data && Array.isArray(response.data.data)) {
          fileData = response.data.data;
          totalCount = response.data.totalCount || 0;
        } else if (response.data && response.data.data === null) {
          setNotFound(true);
          return;
        } else {
          // For "Unexpected response structure", treat as feature off
          setNotFound(true);
          return;
        }

        // Transform API response for the table
        const transformedData = fileData.map(file => ({
          id: file.id,
          name: file.name,
          lastModified: formatLocalDate(file.lastModifiedDate),
          documentType: file.documentType,
          metadata: file.metadata,
          fileLink: file.fileLink
        }));

        setFiles(transformedData);
        setPagination(prev => ({ ...prev, total: totalCount, current: page, pageSize: size }));
      })
      .catch(err => {
        console.error('Error fetching files:', err);
        setNotFound(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [pagination.current, pagination.pageSize]);

  // Initialize on component mount
  useEffect(() => {
    // Skip initialization if feature flag is off
    if (!showPublicFilesLibrary) {
      return;
    }
    fetchFiles(contextPageNo || 1, contextPageSize || 10);
  }, [fetchFiles, contextPageNo, contextPageSize, showPublicFilesLibrary]);

  // Handle pagination change
  const handlePaginationChange = (page, size) => {
    fetchFiles(page, size);

    // Update global context if callback exists
    if (onPaginationChange) {
      onPaginationChange(page, size);
    }
  };

  // Handle file download/view
  const handleFileDownload = (record) => {
    if (record.documentType === 'Link') {
      if (record.fileLink) {
        window.open(record.fileLink, '_blank');
      } else {
        console.error('Link URL not found for:', record.name);
        setErrorModalContent('files.library.error.link.missing');
        setErrorModalOpen(true);
      }
    } else {
      // For file downloads, we need to handle potential errors from the server
      const downloadUrl = `/rest/files/download/${record.id}?organizationId=${orgId}`;

      fetch(downloadUrl)
        .then(response => {
          if (!response.ok) {
            // Check content type of the response
            const contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
              return response.json().then(errorData => {
                throw new Error(errorData.error || 'File download failed');
              });
            } else {
              throw new Error(`Download failed with status: ${response.status}`);
            }
          }
          if (response.redirected) {
            window.location.href = response.url;
            return null;
          }

          // Determine the filename to use for the download
          let filename = record.name;
          // Check if we have original filename in the record metadata
          if (record.metadata && record.metadata.OriginalFileName) {
            filename = record.metadata.OriginalFileName;
          } else {
            // Try to get filename from Content-Disposition header as fallback
            const contentDisposition = response.headers.get('content-disposition');
            if (contentDisposition) {
              const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '');
              }
            }
          }

          // For successful responses with content, convert to blob and download
          return response.blob().then(blob => ({ blob, filename }));
        })
        .then(data => {
          if (data) {
            const { blob, filename } = data;
            // Create download link for the blob
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }
        })
        .catch(error => {
          console.error('Error downloading file:', error);
          // Set appropriate error message based on the error
          let errorMessage = 'files.library.error.download.generic';

          if (error.message.includes('no longer available')) {
            errorMessage = 'files.library.error.file.unavailable';
          } else if (error.message.includes('not found')) {
            errorMessage = 'files.library.error.file.not.found';
          }
          setErrorModalContent(errorMessage);
          setErrorModalOpen(true);
        });
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: <I18N>files.library.name</I18N>,
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div
          className="file-name-container"
          onClick={() => handleFileDownload(record)}
        >
          <FontAwesomeIcon
            icon={getFileIcon(text, record.documentType)}
            className="file-icon"
          />
          <span className="file-name">{text}</span>
        </div>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
      ellipsis: true,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      width: '60%'
    },
    {
      title: <I18N>files.library.last.modified</I18N>,
      dataIndex: 'lastModified',
      key: 'lastModified',
      sorter: (a, b) => new Date(a.lastModified) - new Date(b.lastModified),
      responsive: ['sm', 'md', 'lg', 'xl'],
      width: '40%'
    }
  ];

  // Return NotFound component when flag is Off from global context or when API returns error
  if (!showPublicFilesLibrary || notFound) {
    return <NotFound orgName={orgName} />;
  }

  return (
    <div className="files-library-container">
      <I18NConsumer>
        {() => setPageTitle(`${orgName} - ${i18n['files.library.title.text']}`)}
      </I18NConsumer>

      <div className="files-library-header">
        <h1><I18N>files.library.title.text</I18N></h1>
      </div>

      <div className="files-library-content">
        {isLoading && <Loading />}

        {!isLoading && error && (
          <div className="error-message">
            <p>Error loading files: {error}</p>
          </div>
        )}

        {!isLoading && files && (
          <>
            <Table
              columns={columns}
              dataSource={files}
              rowKey="id"
              pagination={false}
              locale={{
                emptyText: <I18N>files.library.no.files</I18N>
              }}
              className="files-table"
            />

            {files.length > 0 && (
              <div className="text-center pagination-container">
                <EBPagination
                  showTotal={(total, range) => (
                    <I18N>{`event.table.pagination.pageInfo,${range[0]},${range[1]}, ${total}`}</I18N>
                  )}
                  defaultCurrent={1}
                  total={pagination.total}
                  onChange={handlePaginationChange}
                  pageSize={pagination.pageSize}
                  current={pagination.current}
                />
              </div>
            )}
          </>
        )}
      </div>
      <EBModal
        isOpen={errorModalOpen}
        toggleModal={toggleErrorModal}
        type="confirm"
        content={errorModalContent}
        showFooter={true}
        showHeader={true}
        btnConfirmText="OK"
        btnCancelText="Cancel"
        onConfirm={() => fetchFiles(pagination.current, pagination.pageSize)}
      />
    </div>
  );
};

FilesLibrary.propTypes = {
  path: PropTypes.string,
  activeName: PropTypes.string,
};

FilesLibrary.defaultProps = {
  path: '',
  activeName: 'files',
};

export default FilesLibrary;